import {
  forwardRef,
  useId,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import './Input.css';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  /** Shown under the field, and announced when it changes. */
  error?: string | null;
  hint?: string;
  icon?: ReactNode;
  suffix?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, icon, suffix, id, required, className, ...rest },
  ref,
) {
  const generated = useId();
  const inputId = id ?? generated;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;
  const draft = useNumberDraft(rest);

  return (
    <div className={['field', className ?? ''].filter(Boolean).join(' ')}>
      {label ? (
        <label className="field__label" htmlFor={inputId}>
          {label}
          {required ? (
            <span className="field__required" aria-hidden>
              {' '}
              *
            </span>
          ) : null}
        </label>
      ) : null}

      <div className={['field__control', error ? 'field__control--error' : ''].join(' ')}>
        {icon ? <span className="field__icon">{icon}</span> : null}
        <input
          ref={ref}
          id={inputId}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...rest}
          {...draft}
        />
        {suffix ? <span className="field__suffix">{suffix}</span> : null}
      </div>

      {error ? (
        // role="alert" so the message is announced when validation fails,
        // rather than sitting silently below a field the user has left.
        <p className="field__error" id={`${inputId}-error`} role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="field__hint" id={`${inputId}-hint`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
});

/**
 * What a number field shows while someone types in it.
 *
 * A controlled `<input type="number">` handed a number shows that number.
 * Empty it and the handler — `Number(e.target.value)` nearly everywhere here —
 * writes 0 back, so the field showed „0” again: the 0 could not be deleted,
 * only typed over, and „05” became 5 under the cursor. The field now keeps what
 * was typed for as long as it still says the value the form holds, follows the
 * value the moment it changes from elsewhere — „Folosește 900”, a service
 * picked from the price list — and shows the value as held once it is left.
 *
 * Only for a numeric `value`: a field the form already keeps as text is left
 * exactly as it was.
 */
function useNumberDraft({
  type,
  value,
  onChange,
  onBlur,
}: Pick<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange' | 'onBlur'>) {
  const [draft, setDraft] = useState<string | null>(null);
  if (type !== 'number' || typeof value !== 'number') return {};
  const says = (text: string) => (text.trim() === '' ? 0 : Number(text));
  return {
    value:
      draft !== null && says(draft) === value ? draft : Number.isFinite(value) ? String(value) : '',
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      setDraft(e.target.value);
      onChange?.(e);
    },
    onBlur: (e: FocusEvent<HTMLInputElement>) => {
      setDraft(null);
      onBlur?.(e);
    },
  };
}

/**
 * A bare number field, for a table cell or a row of tools where `Input`'s label
 * and frame do not fit — and one that can be emptied, like `Input`.
 */
export const NumberInput = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>
>(function NumberInput(props, ref) {
  const draft = useNumberDraft({ ...props, type: 'number' });
  return <input ref={ref} {...props} type="number" {...draft} />;
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string | null;
  hint?: string;
}

/**
 * The multi-line sibling of Input.
 *
 * Shares the field markup so a note field lines up with the inputs beside it
 * and inherits the same focus ring and error treatment.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, error, hint, id, required, className, rows = 3, ...rest },
  ref,
) {
  const generated = useId();
  const fieldId = id ?? generated;
  const describedBy = error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined;

  return (
    <div className={['field', className ?? ''].filter(Boolean).join(' ')}>
      {label ? (
        <label className="field__label" htmlFor={fieldId}>
          {label}
          {required ? (
            <span className="field__required" aria-hidden>
              {' '}
              *
            </span>
          ) : null}
        </label>
      ) : null}

      <div
        className={[
          'field__control',
          'field__control--area',
          error ? 'field__control--error' : '',
        ].join(' ')}
      >
        <textarea
          ref={ref}
          id={fieldId}
          rows={rows}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...rest}
        />
      </div>

      {error ? (
        <p className="field__error" id={`${fieldId}-error`} role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="field__hint" id={`${fieldId}-hint`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
});
